import { Message } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";

import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import sessionOfferTypes from "astrid-firestore/src/constants/sessionOfferTypes";

import Flex from "../../../../../components/Flex/Flex";
import { useProfile } from "../../../../authentication/state/profile";
import useHasOrganizationRole from "../../../../authorization/hooks/useHasOrganizationRole";
import useRoleTranslations from "../../../../productions/hooks/useRoleTranslations";

import recorderHasDefaultPrice from "../../../helpers/recorderHasDefaultPrice";
import tsToLocaleTimeString from "../../../helpers/tsToLocalTimeString";

import CalendarSessionInformationLabel from "./CalendarSessionInformationLabel";

export default function CalendarSessionInformation({ session, production }) {
	const { t } = useTranslation();
	const user = useProfile();
	const hasOrganizationRole = useHasOrganizationRole({
		user,
		organization: { type: organizationTypes.PRODUCER, id: session.producer },
	});
	const roleTranslations = useRoleTranslations();

	const date = session.start?.toDate()?.toLocaleDateString();
	const start = tsToLocaleTimeString(session.start);
	const end = tsToLocaleTimeString(session.end);

	const translateRecorder = (session) => {
		switch (session.recorder) {
			default:
				return session?.recorderData?.name;
			case sessionOfferTypes.OPEN_POSITION_RECORDER:
				return t("upForGrabs", "Open position");
			case sessionOfferTypes.ANY_RECORDER_IN_TEAM:
			case null:
				return t("anyRecorderFromTeam", "Recording technicians in team");
		}
	};

	const sessionReader = Object.values(session.readerData || {})
		.map((reader) => reader.name)
		.join(", ");

	const sessionManager = session.managers?.map((manager) => manager.name).join(", ");

	if (
		hasOrganizationRole("producerRecorder") &&
		session.recorder !== user.id &&
		!recorderHasDefaultPrice({ production, user })
	) {
		return (
			<Message info>
				{t(
					"recorderMissingDefaultPrice",
					"You do not have a default price and can not view or grab this session",
				)}
			</Message>
		);
	}

	return (
		<Flex alignItems="stretch" style={{ flexDirection: "column", gap: 5, marginBottom: 20 }}>
			{session.first && (
				<CalendarSessionInformationLabel
					icon="flag checkered"
					value={t("firstSessionOnProduction", "First session on production")}
				/>
			)}

			<CalendarSessionInformationLabel
				popupContent={roleTranslations.recorder}
				icon="headphones"
				value={translateRecorder(session)}
			/>

			<CalendarSessionInformationLabel
				popupContent={roleTranslations.reader}
				icon="microphone"
				value={sessionReader}
			/>

			<CalendarSessionInformationLabel
				popupContent={roleTranslations.manager}
				icon="male"
				value={sessionManager}
			/>

			<CalendarSessionInformationLabel
				popupContent={t("studio", "Studio")}
				icon="cube"
				value={session.studioData?.name}
			/>

			<CalendarSessionInformationLabel popupContent={t("date", "Date")} icon="calendar alternate" value={date} />

			<CalendarSessionInformationLabel
				popupContent={t("time", "Time")}
				icon="clock"
				value={`${start} - ${end}`}
			/>
		</Flex>
	);
}
